import { normalizePhone } from "../utils/enquiryPhone";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FloatingSupportChat from "../components/FloatingSupportChat";
import PremiumContactStrip from "../components/PremiumContactStrip";
import ResultsLiveTrustStrip from "../components/ResultsLiveTrustStrip";
import api from "../utils/api";
import { buildWhatsAppQuoteUrl, formatQuotePickupTime, ENQUIRY_RESPONSE_TIME } from "../utils/whatsappQuote";
import { completeEnquiry } from "../utils/enquiryConfirmation";
import { getAttribution, getFunnelJourneyId, trackFunnelEvent, trackFunnelExit } from "../utils/funnelTracking";


const vehicleOrder = ["saloon", "mpv", "nine", "fourteen", "twentyfive"];

const fallbackVehicleDetails = {
  nine: {
    title: "8 Seater Minibus",
    subtitle: "Perfect for small groups and families",
    image: "/minibus.jpg",
    badge: "Most popular",
    badgeTone: "green",
    passengers: "8 Passengers",
    bags: "8 Large Bags",
    highlight: "Ideal for families, small groups and business travel",
    similar: "Mercedes V-Class or similar",
    proof: "Best price guarantee",
    bullets: ["Instant confirmation", "Free 60-min waiting time", "No hidden charges"],
  },
  fourteen: {
    title: "16 Seater Coach",
    subtitle: "Spacious and comfortable for bigger groups",
    image: "/coach.jpeg",
    badge: "Great for larger groups",
    badgeTone: "rose",
    passengers: "16 Passengers",
    bags: "16 Large Bags",
    highlight: "Perfect for large groups, events and corporate travel",
    similar: "Mercedes Sprinter or similar",
    proof: "Great for large groups",
    bullets: ["Priority vehicle allocation", "Free 60-min waiting time", "Flexible cancellation"],
  },
  twentyfive: {
    title: "25 Seater Coach",
    subtitle: "Coordinated travel for larger airport groups",
    image: "/coach-hero.png",
    badge: "Coach hire",
    badgeTone: "rose",
    passengers: "25+ Passengers",
    bags: "Group Luggage",
    highlight: "Best for schools, teams, tours and company airport transfers",
    similar: "Executive mini coach or similar",
    proof: "Partner allocation confirmed",
    bullets: ["No payment taken now", "Operator confirmation", "Group support included"],
  },
};

const formatPostcode = (pc = "") => {
  const text = String(pc || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ")
    .replace(/\s*,\s*/g, ", ");
  const compact = text.replace(/\s+/g, "");
  if (/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(compact)) {
    return `${compact.slice(0, compact.length - 3)} ${compact.slice(-3)}`;
  }
  return text;
};

const formatJourneyLocation = (value, config = {}) => {
  const formatted = formatPostcode(value);
  if (!config.heathrowFriendlyLabels) return formatted;
  const compact = formatted.replace(/[^A-Z0-9]/g, "");
  if (compact.startsWith("TW6") || formatted === "HOUNSLOW, UK" || formatted === "HOUNSLOW") {
    return "Heathrow Airport";
  }
  return formatted;
};

const formatPrice = (card) => {
  if (!card?.available) return "Not available";
  if (card.price == null) return card.book_mode === "enquiry" ? "Estimated price" : "Price on enquiry";
  const amount = Number(card.price);
  const formatted = Number.isFinite(amount)
    ? new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount)
    : `£${card.price}`;
  return card.book_mode === "enquiry" ? `Estimated from ${formatted}` : formatted;
};

const formatMoney = (value) => {
  if (value == null) return "Price on enquiry";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return `£${value}`;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const roundMoney = (value) => Math.round(Number(value) * 100) / 100;

const getDisplayPricing = (card, config = {}) => {
  const originalPrice = card?.price == null ? null : Number(card.price);
  const discountPercent = Math.max(0, Number(config.estimateDiscountPercent) || 0);
  if (!Number.isFinite(originalPrice) || originalPrice <= 0 || discountPercent <= 0) {
    return {
      originalPrice: Number.isFinite(originalPrice) ? roundMoney(originalPrice) : null,
      displayPrice: Number.isFinite(originalPrice) ? roundMoney(originalPrice) : card?.price ?? null,
      discountPercent: 0,
      adjusted: false,
    };
  }
  return {
    originalPrice: roundMoney(originalPrice),
    displayPrice: roundMoney(originalPrice * (1 - discountPercent / 100)),
    discountPercent,
    adjusted: true,
  };
};

const buildDiscountNudge = (quote, config = {}) => {
  if (!config.discountNudgeEnabled) return "";
  const pickup = formatPostcode(quote?.request?.pickup);
  const dropoff = formatPostcode(quote?.request?.dropoff);
  const vehicleLabel = config.discountNudgeVehicleLabel || "fare";
  if (pickup && dropoff) {
    if (config.discountNudgeActionLabel) {
      return `${pickup} → ${dropoff} may qualify for a better ${vehicleLabel}.`;
    }
    return `${pickup} to ${dropoff} may qualify for a better ${vehicleLabel}. Send an enquiry and our team will check today’s best available rate.`;
  }
  if (pickup) {
    if (config.discountNudgeActionLabel) {
      return `${pickup} may qualify for a better ${vehicleLabel}.`;
    }
    return `${pickup} may qualify for a better ${vehicleLabel}. Send an enquiry and our team will check today’s best available rate.`;
  }
  if (config.discountNudgeActionLabel) {
    return `This journey may qualify for a better ${vehicleLabel}.`;
  }
  return `This journey may qualify for a better ${vehicleLabel}. Send an enquiry and our team will check today’s best available rate.`;
};

const getPublicPricePolicy = (card, quote, config = {}) => {
  if (config.showPublicPricing === false) {
    return { visible: false, reason: config.publicPriceReason || "brand_enquiry_only" };
  }
  if (typeof config.publicPricingPolicy === "function") {
    const policy = config.publicPricingPolicy(card, quote);
    if (typeof policy === "boolean") return { visible: policy, reason: policy ? "public_price" : "pricing_review" };
    if (policy && typeof policy.visible === "boolean") return policy;
  }
  return { visible: true, reason: "public_price" };
};

const buildDisplayCard = (card, config = {}, quote = null) => {
  if (!card) return null;
  const displayPricing = getDisplayPricing(card, config);
  const detailsKey = card.details_key || card.code;
  const details = {
    ...fallbackVehicleDetails[detailsKey],
    ...(config.vehicles?.[detailsKey] || {}),
    ...(card.option_details || {}),
  };
  const publicPricePolicy = getPublicPricePolicy(card, quote, config);
  const publicPriceVisible = publicPricePolicy.visible;
  return {
    ...card,
    display_title: details.title || card.label,
    price: publicPriceVisible ? displayPricing.displayPrice : null,
    book_mode: resolveConfigValue(config.forceEnquiryOnly, { card, quote }) ? "enquiry" : card.book_mode,
    pricing_display: {
      original_estimate: displayPricing.originalPrice,
      displayed_estimate: publicPriceVisible ? displayPricing.displayPrice : null,
      discount_percent: publicPriceVisible ? displayPricing.discountPercent : 0,
      adjusted: publicPriceVisible ? displayPricing.adjusted : false,
      public_price_visible: publicPriceVisible,
      public_price_reason: publicPricePolicy.reason,
      final_price_confirmed_by_ops: true,
    },
  };
};

const formatReason = (card) => {
  if (!card) return "";
  if (card.reason) return card.reason;
  if (!card.available) return "Not available for this journey";
  if (card.book_mode === "enquiry") return "Confirmation required before booking";
  return "Fixed all-inclusive price";
};

const getFilters = (search) => {
  const params = new URLSearchParams(search);
  const raw = params.get("vehicles") || params.get("vehicle") || params.get("onlyVehicle") || params.get("onlyVehicles");
  if (!raw) return [];
  return raw.split(",").map((item) => item.trim()).filter(Boolean);
};

const toInputDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};

const defaultPickupTime = () => {
  const date = new Date(Date.now() + 4.5 * 60 * 60 * 1000);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || "").trim());

const CONTACT_PREFERENCE_ENABLED =
  process.env.REACT_APP_ENQUIRY_CONTACT_PREFERENCE !== "false";
const DRAFT_CAPTURE_ENABLED =
  process.env.REACT_APP_ENQUIRY_DRAFT_CAPTURE !== "false";

const resolveConfigValue = (value, context) =>
  typeof value === "function" ? value(context) : value;

const PHONE_COUNTRIES = [
  { value: "GB", label: "United Kingdom (+44)" },
  { value: "IE", label: "Ireland (+353)" },
  { value: "US", label: "United States / Canada (+1)" },
  { value: "IN", label: "India (+91)" },
  { value: "PK", label: "Pakistan (+92)" },
  { value: "BD", label: "Bangladesh (+880)" },
  { value: "AE", label: "United Arab Emirates (+971)" },
  { value: "SA", label: "Saudi Arabia (+966)" },
  { value: "OTHER", label: "Other country (enter + code)" },
];

const CONTACT_OPTIONS = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "phone", label: "Call" },
  { value: "email", label: "Email" },
];


const isPossiblePhone = (value, country = "GB") => normalizePhone(value, country).possible;

const createDraftToken = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const value = Math.floor(Math.random() * 16);
    return (char === "x" ? value : (value & 0x3) | 0x8).toString(16);
  });
};

const GOOGLE_REVIEW_URL = "https://share.google/trIAb3GAvw55L1l8S";

const TrustItem = ({ icon, title, body, href }) => (
  <article className="premium-results-trust-item">
    <b>{icon}</b>
    <span>
      <strong>{title}</strong>
      <small>
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer">{body}</a>
        ) : body}
      </small>
    </span>
  </article>
);

const PremiumQuoteResults = ({ config }) => {
  const navigate = useNavigate();
  const { quoteId, token } = useParams();
  const location = useLocation();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [enquiry, setEnquiry] = useState(null);
  const [enquiryStep, setEnquiryStep] = useState(0);
  const [enquiryForm, setEnquiryForm] = useState({});
  const [enquiryError, setEnquiryError] = useState("");
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);
  const [enquirySuccess, setEnquirySuccess] = useState(null);
  const enquiryDraftTokenRef = useRef("");
  const resultActionTakenRef = useRef(false);
  const seenResultCardsRef = useRef(new Set());
  const configuredContactPreference = resolveConfigValue(
    config.contactPreferenceEnabled,
    { quote }
  );
  // Shared defaults for every quote enquiry, independent of route or brand.
  const enquiryImprovements = process.env.REACT_APP_QUOTE_ENQUIRY_V2 !== "false" &&
    (resolveConfigValue(config.enquiryImprovementsEnabled, { quote }) ?? true);
  const whatsappQuoteEnabled = process.env.REACT_APP_QUOTE_WHATSAPP_CTA !== "false" &&
    (resolveConfigValue(config.whatsappQuoteEnabled, { quote }) ?? true);
  const contactPreferenceEnabled = enquiryImprovements ? false :
    (configuredContactPreference ?? CONTACT_PREFERENCE_ENABLED);
  const savedEnquiryRef = useRef(null);
  const flowVersion = enquiryImprovements ? "quote_contact_v2" : "existing";
  const ctaVersion = whatsappQuoteEnabled ? "whatsapp_v1" : "existing";

  const vehicleFilters = useMemo(() => getFilters(location.search), [location.search]);

  useEffect(() => {
    const fetchQuote = async () => {
      if (!quoteId || !token) {
        setError("Quote details missing. Please start a new quote.");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/api/quotes-v2/${quoteId}/${token}`);
        if (res.data?.vehicles) {
          setQuote(res.data);
        } else {
          setError(res.data?.error || "Unable to fetch quote. Please try again.");
        }
      } catch (err) {
        setError(err.response?.data?.error || "Unable to fetch quote. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [quoteId, token]);

  useEffect(() => {
    if (!config.funnelTrackingEnabled || !quote?.quote_id) return;
    trackFunnelEvent("quote_results_view", {
      flow_version: flowVersion, cta_version: ctaVersion,
      brand: config.chatBrand || "ccg",
      quote_id: quote.quote_id,
      vehicle_count: quote.vehicles?.length || 0,
    });
  }, [config.chatBrand, config.funnelTrackingEnabled, quote?.quote_id, quote?.vehicles?.length, flowVersion, ctaVersion]);

  useEffect(() => {
    if (!config.funnelTrackingEnabled || !quote?.quote_id) return undefined;
    const recordExit = () => {
      if (resultActionTakenRef.current) return;
      trackFunnelExit("results_abandon", {
        brand: config.chatBrand || "ccg",
        quote_id: quote.quote_id,
        status: seenResultCardsRef.current.size ? "card_seen_no_action" : "no_card_seen",
        vehicle_count: seenResultCardsRef.current.size,
      });
    };
    window.addEventListener("pagehide", recordExit);
    return () => window.removeEventListener("pagehide", recordExit);
  }, [config.chatBrand, config.funnelTrackingEnabled, quote?.quote_id]);

  const orderedVehicles = useMemo(() => {
    if (!quote?.vehicles) return [];
    const sorted = [...quote.vehicles].sort((a, b) => vehicleOrder.indexOf(a.code) - vehicleOrder.indexOf(b.code));
    const filters = config.forceVehicleSet?.length
      ? config.forceVehicleSet
      : vehicleFilters.length
        ? vehicleFilters
        : config.defaultVehicles || [];
    if (!filters.length) return sorted;
    const filtered = sorted.filter((vehicle) => filters.includes(vehicle.code));
    return filtered.length ? filtered : sorted;
  }, [config.defaultVehicles, config.forceVehicleSet, quote?.vehicles, vehicleFilters]);

  const resultCards = useMemo(() => {
    if (!orderedVehicles.length) return [];
    const requestableVehicleCodes = config.forceRequestableVehicleCodes || [];
    const baseCards = orderedVehicles.map((vehicle) =>
      requestableVehicleCodes.includes(vehicle.code)
        ? { ...vehicle, quote_available: vehicle.available !== false, available: true }
        : vehicle
    );
    const additionalCards = (config.additionalEnquiryCards || []).flatMap((option) => {
      const source =
        baseCards.find((vehicle) => vehicle.code === option.sourceVehicleCode) ||
        quote?.vehicles?.find((vehicle) => vehicle.code === option.sourceVehicleCode);
      if (!source) return [];
      return [{
        ...source,
        code: option.vehicleCode || source.code,
        display_key: option.id,
        details_key: option.detailsKey || option.id,
        option_details: option.details,
        enquiry_intent: {
          id: option.id,
          note: option.note,
          requestedOptions: option.requestedOptions || [],
        },
      }];
    });
    return [...baseCards, ...additionalCards];
  }, [config.additionalEnquiryCards, config.forceRequestableVehicleCodes, orderedVehicles, quote?.vehicles]);

  useEffect(() => {
    if (!config.funnelTrackingEnabled || !quote?.quote_id || typeof IntersectionObserver === "undefined") return undefined;
    const targets = Array.from(document.querySelectorAll("[data-funnel-result-card]"));
    const timers = new Map();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const key = entry.target.getAttribute("data-funnel-result-card");
        if (!key || seenResultCardsRef.current.has(key)) return;
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
          if (timers.has(key)) window.clearTimeout(timers.get(key));
          timers.delete(key);
          return;
        }
        timers.set(key, window.setTimeout(() => {
          if (seenResultCardsRef.current.has(key)) return;
          seenResultCardsRef.current.add(key);
          const publicPriceVisible = entry.target.getAttribute("data-funnel-price-visible") === "true";
          const publicPriceReason = entry.target.getAttribute("data-funnel-price-reason") || undefined;
          const details = {
            flow_version: flowVersion, cta_version: ctaVersion,
            brand: config.chatBrand || "ccg",
            quote_id: quote.quote_id,
            vehicle_code: entry.target.getAttribute("data-funnel-vehicle"),
            fare: entry.target.getAttribute("data-funnel-fare") || undefined,
            visibility_ms: 1000,
            public_price_visible: publicPriceVisible,
            public_price_reason: publicPriceReason,
          };
          trackFunnelEvent("result_card_view", details);
          if (publicPriceVisible) trackFunnelEvent("vehicle_price_view", details);
          observer.unobserve(entry.target);
          timers.delete(key);
        }, 1000));
      });
    }, { threshold: [0.5] });
    targets.forEach((target) => observer.observe(target));
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      observer.disconnect();
    };
  }, [config.chatBrand, config.funnelTrackingEnabled, quote?.quote_id, resultCards.length, flowVersion, ctaVersion]);

  const vehicleCount = resultCards.filter((vehicle) => vehicle.available !== false).length || resultCards.length;
  const headline =
    config.resultsHeadline ||
    (vehicleCount === 1
      ? "We've found 1 great option for your journey"
      : `We've found ${vehicleCount} great options for your journey`);
  const inclusions = config.inclusions || [
    "▱ All prices include VAT and airport charges",
    "◴ Free changes up to 24 hours before travel",
    "▱ Safe, reliable and on-time guarantee",
  ];
  const proofStats = config.proofStats || [
    { value: "10,000+", label: "Happy Customers" },
    { value: "98%", label: "On-time arrivals" },
    { value: "4.8 ★★★★★", label: "Google rating" },
  ];

  const handleSelect = (card) => {
    if (!card?.available || !quote?.quote_id) return;
    resultActionTakenRef.current = true;
    if (config.funnelTrackingEnabled) {
      trackFunnelEvent("vehicle_cta_click", {
        flow_version: flowVersion, cta_version: ctaVersion, brand: config.chatBrand || "ccg", quote_id: quote.quote_id,
        vehicle_code: card.code, action: "continue", fare: Number(card.price || 0),
      });
    }
    const bookingPath = config.bookingPath || "/book";
    const handoffSearch = typeof window !== "undefined" ? window.location.search : "";
    navigate(`${bookingPath}/${quote.quote_id}/${token}/${card.code}${handoffSearch}`);
  };

  const openEnquiry = (card, mode = "enquiry", intent = null) => {
    if (!card?.available) return;
    resultActionTakenRef.current = true;
    if (config.funnelTrackingEnabled) {
      trackFunnelEvent(mode === "callback" ? "callback_click" : "enquiry_flow_open", {
        flow_version: flowVersion, cta_version: ctaVersion, brand: config.chatBrand || "ccg", quote_id: quote?.quote_id,
        vehicle_code: card.code, intent: intent?.id || mode,
        cta_type: mode === "callback" ? "callback" : mode === "quick_enquiry" ? "quick_enquiry" : "fare_request",
        fare: Number(card.price || card.pricing_display?.displayed_estimate || 0),
        public_price_visible: card.pricing_display?.public_price_visible !== false,
        public_price_reason: card.pricing_display?.public_price_reason,
      });
    }
    const defaultNote =
      intent?.note
        ? intent.note
        : mode === "callback"
        ? "Please call me back about this option."
        : card.book_mode === "enquiry"
          ? "I would like to confirm availability for this option."
          : "";
    const saved = savedEnquiryRef.current;
    if (enquiryImprovements && saved?.quoteId === quote?.quote_id && saved?.vehicleCode === card.code) {
      setEnquiry({ card, mode, intent });
      setEnquiryForm(saved.form);
      setEnquiryStep(saved.step);
      enquiryDraftTokenRef.current = saved.draftToken;
      setEnquiryError("");
      setEnquirySuccess(null);
      return;
    }
    savedEnquiryRef.current = null;
    setEnquiry({ card, mode, intent });
    setEnquiryStep(0);
    setEnquiryError("");
    setEnquirySuccess(null);
    enquiryDraftTokenRef.current = createDraftToken();
    const handoff = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    setEnquiryForm({
      name: "",
      email: "",
      phone: "",
      phone_country: "GB",
      preferred_contact_methods: contactPreferenceEnabled ? ["whatsapp"] : [],
      pickup_time: toInputDateTime(quote?.request?.pickup_time) || defaultPickupTime(),
      flight_number: handoff.get("flight_number") || "",
      passengers: card.quote_available === false ? "" : quote?.request?.passengers || "",
      bags: quote?.request?.bags ?? "",
      notes: defaultNote,
      return_requested: false,
      confirmed_counts: {},
    });
  };

  const closeEnquiry = async () => {
    if (enquirySubmitting) return;
    if (enquiryImprovements && enquiry && !enquirySuccess) {
      savedEnquiryRef.current = { quoteId: quote?.quote_id, vehicleCode: enquiry.card.code,
        form: enquiryForm, step: enquiryStep, draftToken: enquiryDraftTokenRef.current };
    }
    if (config.funnelTrackingEnabled && enquiry && !enquirySuccess) {
      trackFunnelEvent("enquiry_flow_abandon", {
        flow_version: flowVersion, cta_version: ctaVersion, brand: config.chatBrand || "ccg", quote_id: quote?.quote_id,
        vehicle_code: enquiry.card?.code, step: currentEnquiryStep?.key,
        step_number: enquiryStep + 1, status: "closed",
      });
    }
    if (DRAFT_CAPTURE_ENABLED && enquiry && !enquirySuccess && enquiryDraftTokenRef.current) {
      try {
        await saveEnquiryDraft({ status: "abandoned", currentStep: currentEnquiryStep?.key });
      } catch (_) {
        // Draft capture must never trap the customer in the modal.
      }
    }
    setEnquiry(null);
    setEnquiryError("");
    setEnquirySuccess(null);
  };

  const enquirySteps = useMemo(() => contactPreferenceEnabled ? [
    {
      key: "group_details",
      label: "Confirm your group",
      fields: [
        { key: "passengers", label: "Passengers", type: "number", min: 1 },
        { key: "bags", label: "Bags", type: "number", min: 0 },
      ],
    },
    {
      key: "journey_timing",
      label: "Confirm journey timing",
      fields: [
        { key: "pickup_time", label: "Pickup date and time", type: "datetime-local" },
        { key: "flight_number", label: "Flight number", type: "text", optional: true, placeholder: "Optional, e.g. BA123" },
      ],
    },
    {
      key: "journey_requirements",
      label: "Anything else we should check?",
      fields: [{
        key: "notes", label: "Journey requirements", type: "textarea", optional: true,
        placeholder: "Optional: return journey, child seats, accessibility needs, extra stop or other requirements",
      }],
    },
    {
      key: "contact_details",
      label: "Where should we send your confirmed price?",
      contact: true,
      fields: [],
    },
  ] : [
    { key: "passengers", label: "Passengers", type: "number", min: 1 },
    { key: "bags", label: "Bags", type: "number", min: 0 },
    { key: "pickup_time", label: "Pickup time", type: "datetime-local" },
    { key: "flight_number", label: "Flight number", type: "text", optional: true, placeholder: "Optional, e.g. BA123" },
    { key: "notes", label: "Return or special requirements", type: "textarea", optional: true, placeholder: "Optional: return journey, child seats, extra stop, accessibility needs or anything else" },
    { key: "name", label: "Your name", type: "text", placeholder: "Full name" },
    { key: "phone", label: "Phone", type: "tel", placeholder: "Mobile number" },
    { key: "email", label: "Email", type: "email", placeholder: "you@example.com" },
  ], [contactPreferenceEnabled]);

  const currentEnquiryStep = enquirySteps[enquiryStep];
  const enquiryAtLastStep = enquiryStep >= enquirySteps.length - 1;

  useEffect(() => {
    if (!config.funnelTrackingEnabled || !enquiry || enquirySuccess || !currentEnquiryStep) return;
    trackFunnelEvent("enquiry_step_view", {
      flow_version: flowVersion, cta_version: ctaVersion, brand: config.chatBrand || "ccg", quote_id: quote?.quote_id,
      vehicle_code: enquiry.card?.code, step: currentEnquiryStep.key,
      step_number: enquiryStep + 1, mode: enquiry.mode,
    });
    if (currentEnquiryStep.contact) {
      trackFunnelEvent("enquiry_contact_reassurance_view", {
        flow_version: flowVersion, cta_version: ctaVersion, brand: config.chatBrand || "ccg", quote_id: quote?.quote_id,
        vehicle_code: enquiry.card?.code, mode: enquiry.mode,
      });
    }
  }, [config.chatBrand, config.funnelTrackingEnabled, currentEnquiryStep, enquiry, enquiryStep, enquirySuccess, quote?.quote_id]);

  const updateEnquiryField = (key, value) => {
    setEnquiryForm((prev) => ({ ...prev, [key]: value }));
    setEnquiryError("");
  };

  const validateField = (step) => {
    const value = String(enquiryForm[step.key] ?? "").trim();
    if (step.optional && !value) return "";
    if (!value) return `${step.label} is required.`;
    if (step.key === "email" && !isValidEmail(value)) return "Please enter a valid email address.";
    if (step.key === "phone" && !isPossiblePhone(value, enquiryForm.phone_country)) return "Please enter a complete phone number, including the country code when outside the UK.";
    if (step.key === "pickup_time") {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "Please choose a valid pickup time.";
      if (date.getTime() < Date.now() + 4 * 60 * 60 * 1000 - 60 * 1000) return "Pickup time must be at least 4 hours from now.";
    }
    if (step.key === "passengers" && Number(value) < 1) return "Please enter at least 1 passenger.";
    if (step.key === "bags" && Number(value) < 0) return "Please enter 0 or more bags.";
    return "";
  };

  const validateEnquiryStep = () => {
    const step = currentEnquiryStep;
    if (!step) return "";
    if (step.contact) {
      if (!String(enquiryForm.name || "").trim()) return "Your name is required.";
      if (!isPossiblePhone(enquiryForm.phone, enquiryForm.phone_country)) {
        return "Please enter a complete phone number, including the country code when outside the UK.";
      }
      if (!isValidEmail(enquiryForm.email)) return "Please enter a valid email address.";
      const methods = enquiryForm.preferred_contact_methods || [];
      if (!methods.length) return "Please choose at least one contact method.";
      return "";
    }
    const fields = step.fields || [step];
    for (const field of fields) {
      const error = validateField(field);
      if (error) return error;
    }
    return "";
  };

  // Opt-in switches: legacy result-card WhatsApp remains separately controlled.
  const handoffEnabled = process.env.REACT_APP_WHATSAPP_HANDOFF_ENABLED === "true";
  const inFlowWhatsAppEnabled = handoffEnabled && process.env.REACT_APP_WHATSAPP_IN_FLOW === "true";
  const handoffTokens = useRef({});
  const handoffTokenFor = (card) => {
    const key = `${quote?.quote_id}:${card.code}`;
    if (!handoffTokens.current[key]) {
      try { handoffTokens.current[key] = sessionStorage.getItem(`wa_handoff:${key}`); } catch {}
      if (!handoffTokens.current[key]) handoffTokens.current[key] = createDraftToken();
      try { sessionStorage.setItem(`wa_handoff:${key}`, handoffTokens.current[key]); } catch {}
    }
    return handoffTokens.current[key];
  };
  const availableFormFor = (card) => {
    const saved = savedEnquiryRef.current;
    return saved?.quoteId === quote?.quote_id && saved?.vehicleCode === card.code ? saved.form : null;
  };
  const whatsAppHref = (card, form = null) => buildWhatsAppQuoteUrl({
    pickup: quote.request?.pickup, dropoff: quote.request?.dropoff,
    pickupTime: form?.pickup_time || quote.request?.pickup_time,
    brand: config.chatBrand, vehicle: card.display_title || card.label, quoteId: quote.quote_id,
    passengers: form?.confirmed_counts?.passengers ? form.passengers : null,
    bags: form?.confirmed_counts?.bags ? form.bags : null,
    returnRequested: form?.return_requested, notes: form?.notes,
    handoffReference: handoffEnabled ? handoffTokenFor(card) : undefined,
  });
  const recordWhatsApp = (card, form = null, step = null, entryPoint = "result_card") => {
    resultActionTakenRef.current = true;
    trackFunnelExit("whatsapp_click", { brand: config.chatBrand || "ccg", quote_id: quote.quote_id,
      vehicle_code: card.code, step_name: step, cta_type: "whatsapp_quote",
      source: entryPoint, conversion_role: "engagement" });
    if (!handoffEnabled) return;
    // Keep the navigation synchronous for mobile app links; the bounded snapshot travels using keepalive.
    const body = { handoff_token: handoffTokenFor(card), quote_id: quote.quote_id, access_token: token,
      vehicle_code: card.code, vehicle: card.display_title || card.label, brand: config.chatBrand,
      draft_token: form ? enquiryDraftTokenRef.current : null, form: form || {},
      entry_point: entryPoint, step_name: step,
      attribution: { ...getAttribution(), journey_id: getFunnelJourneyId(), source_url: window.location.href } };
    fetch(`${api.defaults.baseURL.replace(/\/$/, "")}/api/whatsapp-handoffs-v1`, {
      method: "POST", credentials: "include", keepalive: true,
      headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    }).catch(() => {});
    if (form) saveEnquiryDraft({ currentStep: step }).catch(() => {});
  };

  const saveEnquiryDraft = async ({ status = "active", lastCompletedStep = null, currentStep = null } = {}) => {
    if (!DRAFT_CAPTURE_ENABLED || !enquiry?.card || !quote?.quote_id || !enquiryDraftTokenRef.current) return;
    const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    await api.post("/api/enquiry-drafts-v2", {
      draft_token: enquiryDraftTokenRef.current,
      quote_id: quote.quote_id,
      access_token: token,
      brand: config.chatBrand || "ccg",
      vehicle_code: enquiry.card.code,
      enquiry_mode: enquiry.mode,
      ...enquiryForm,
      preferred_contact_method: contactPreferenceEnabled
        ? (enquiryForm.preferred_contact_methods || []).join(",") || null
        : null,
      last_completed_step: lastCompletedStep,
      current_step: currentStep,
      status,
      attribution: {
        ...getAttribution(),
        source_url: typeof window !== "undefined" ? window.location.href : "",
        gclid: params.get("gclid") || getAttribution().gclid || undefined,
        wbraid: params.get("wbraid") || getAttribution().wbraid || undefined,
        gbraid: params.get("gbraid") || getAttribution().gbraid || undefined,
      },
    });
  };

  useEffect(() => {
    if (!DRAFT_CAPTURE_ENABLED || !enquiry || enquirySuccess || !enquiryDraftTokenRef.current) return undefined;
    const timer = window.setTimeout(() => {
      saveEnquiryDraft({ status: "active", currentStep: currentEnquiryStep?.key }).catch(() => {});
    }, 650);
    return () => window.clearTimeout(timer);
  }, [currentEnquiryStep?.key, enquiry, enquiryForm, enquirySuccess]);

  const buildEnquiryNotes = () => {
    const sourceUrl = typeof window !== "undefined" ? window.location.href : "";
    const handoff = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    const userNotes = String(enquiryForm.notes || "").trim();
    const pricingDisplay = enquiry?.card?.pricing_display || null;
    const pricingLines = pricingDisplay
      ? [
          `Original system estimate: ${formatMoney(pricingDisplay.original_estimate)}`,
          `Displayed customer estimate: ${formatMoney(pricingDisplay.displayed_estimate)}`,
          `Display discount: ${pricingDisplay.discount_percent || 0}%`,
          "Final fare to be confirmed by support/operator.",
        ]
      : [];
    const lines = [
      userNotes,
      ...(enquiryImprovements && enquiryForm.return_requested ? ["Return journey requested — confirm return details with customer."] : []),
      "",
      `Result action: ${enquiry?.mode === "callback" ? "Request callback" : enquiry?.mode === "quick_enquiry" ? "Send a quick enquiry" : "Check best available price"}`,
      ...(enquiry?.intent?.id ? [`Enquiry intent: ${enquiry.intent.id}`] : []),
      ...(enquiry?.intent?.requestedOptions?.length
        ? [`Requested options: ${enquiry.intent.requestedOptions.join(", ")}`]
        : []),
      `Brand: ${config.brandName}`,
      ...(enquiryForm.flight_number ? [`Flight: ${enquiryForm.flight_number}`] : []),
      ...(handoff.get("flight_time") ? [`Flight time: ${handoff.get("flight_time")}`] : []),
      ...(handoff.get("airport_code") ? [`Airport: ${handoff.get("airport_code")}${handoff.get("terminal") ? ` Terminal ${handoff.get("terminal")}` : ""}`] : []),
      ...(handoff.get("direction") ? [`Direction: ${handoff.get("direction")}`] : []),
      ...(handoff.get("ajp_journey_id") ? [`AJP journey: ${handoff.get("ajp_journey_id")}`] : []),
      ...pricingLines,
      `Source URL: ${sourceUrl}`,
    ].filter((line, index) => line || index !== 0);
    return lines.join("\n").trim();
  };

  const submitEnquiry = async () => {
    if (!enquiry?.card || !quote?.quote_id) return;
    setEnquirySubmitting(true);
    setEnquiryError("");
    try {
      const res = await api.post("/api/enquiries-v2", {
        quote_id: quote.quote_id,
        access_token: token,
        vehicle_code: enquiry.card.code,
        name: enquiryForm.name,
        email: enquiryForm.email,
        phone: enquiryForm.phone,
        phone_country: enquiryForm.phone_country,
        preferred_contact_method: contactPreferenceEnabled ? (enquiryForm.preferred_contact_methods || []).join(",") : undefined,
        enquiry_draft_token: DRAFT_CAPTURE_ENABLED ? enquiryDraftTokenRef.current : undefined,
        passengers: enquiryForm.passengers,
        bags: enquiryForm.bags,
        pickup_time: enquiryForm.pickup_time,
        flight_number: enquiryForm.flight_number || undefined,
        notes: buildEnquiryNotes(),
        extras: {
          brand: config.brandName,
          source_url: typeof window !== "undefined" ? window.location.href : "",
          enquiry_action: enquiry.mode,
          flight_number: enquiryForm.flight_number || undefined,
          contact_flow_v2: enquiryImprovements,
          ...(enquiry.intent?.id
            ? {
                enquiry_intent: enquiry.intent.id,
                requested_options: enquiry.intent.requestedOptions || [],
              }
            : {}),
          pricing_display: enquiry.card.pricing_display || null,
        },
      });
      const confirmation = {
        contactFlowV2: enquiryImprovements,
        returnRequested: enquiryImprovements && !!enquiryForm.return_requested,
        refCode: res.data?.ref_code || "ENQ",
        brand: config.brandName,
        pickup: quote.request?.pickup,
        dropoff: quote.request?.dropoff,
        passengers: enquiryForm.passengers,
        bags: enquiryForm.bags,
        pickupTime: enquiryForm.pickup_time,
        vehicle: enquiry.card.display_title || enquiry.card.label,
      };
      savedEnquiryRef.current = null;
      setEnquirySuccess(confirmation);
      if (config.funnelTrackingEnabled) {
        trackFunnelExit("enquiry_complete", {
          flow_version: flowVersion, cta_version: ctaVersion, brand: config.chatBrand || "ccg", quote_id: quote.quote_id,
          vehicle_code: enquiry.card.code, mode: enquiry.mode, status: "ok",
        });
      }
      completeEnquiry(navigate, confirmation);
    } catch (err) {
      setEnquiryError(err.response?.data?.error || "Could not send this enquiry. Please try again.");
    } finally {
      setEnquirySubmitting(false);
    }
  };

  const nextEnquiryStep = () => {
    const validationError = validateEnquiryStep();
    if (validationError) {
      if (config.funnelTrackingEnabled) {
        trackFunnelEvent("booking_validation_error", {
          flow_version: flowVersion, cta_version: ctaVersion, brand: config.chatBrand || "ccg", quote_id: quote?.quote_id,
          vehicle_code: enquiry?.card?.code, step: currentEnquiryStep?.key,
          step_number: enquiryStep + 1, error_type: validationError,
        });
      }
      setEnquiryError(validationError);
      return;
    }
    if (enquiryAtLastStep) {
      if (config.funnelTrackingEnabled) {
        trackFunnelEvent("booking_submit_click", {
          flow_version: flowVersion, cta_version: ctaVersion, brand: config.chatBrand || "ccg", quote_id: quote?.quote_id,
          vehicle_code: enquiry?.card?.code, mode: enquiry?.mode,
        });
      }
      submitEnquiry();
      return;
    }
    if (config.funnelTrackingEnabled) {
      trackFunnelEvent("enquiry_step_complete", {
        flow_version: flowVersion, cta_version: ctaVersion, brand: config.chatBrand || "ccg", quote_id: quote?.quote_id,
        vehicle_code: enquiry?.card?.code, step: currentEnquiryStep?.key,
        step_number: enquiryStep + 1, mode: enquiry?.mode,
      });
    }
    saveEnquiryDraft({
      status: "active",
      lastCompletedStep: currentEnquiryStep?.key,
      currentStep: enquirySteps[enquiryStep + 1]?.key || null,
    }).catch(() => {});
    if (enquiryImprovements && ["passengers", "bags"].includes(currentEnquiryStep.key)) {
      setEnquiryForm(prev => ({ ...prev, confirmed_counts: { ...prev.confirmed_counts, [currentEnquiryStep.key]: true } }));
    }
    setEnquiryStep((step) => step + 1);
  };

  const backEnquiryStep = () => {
    setEnquiryError("");
    setEnquiryStep((step) => Math.max(0, step - 1));
  };

  const renderEnquiryField = (field) => {
    const headingAlreadyLabelsField =
      !currentEnquiryStep?.fields &&
      currentEnquiryStep?.key === field.key &&
      currentEnquiryStep?.label === field.label;

    return (
    <label key={field.key}>
      {!headingAlreadyLabelsField ? <span>{field.label}{field.optional ? " (optional)" : ""}</span> : null}
      {field.key === "phone" && !currentEnquiryStep.contact && (
        <select aria-label="Phone country" value={enquiryForm.phone_country || "GB"}
          onChange={event => updateEnquiryField("phone_country", event.target.value)}>
          {PHONE_COUNTRIES.map(country => <option key={country.value} value={country.value}>{country.label}</option>)}
        </select>
      )}
      {field.type === "textarea" ? (
        <textarea
          aria-label={field.label}
          rows={4}
          value={enquiryForm[field.key] || ""}
          placeholder={field.placeholder}
          onChange={(event) => updateEnquiryField(field.key, event.target.value)}
        />
      ) : (
        <input
          aria-label={field.label}
          type={field.type}
          autoComplete={enquiryImprovements ? ({ name: "name", phone: "tel", email: "email" }[field.key]) : undefined}
          min={field.min}
          value={enquiryForm[field.key] ?? ""}
          placeholder={field.placeholder}
          inputMode={field.type === "number" ? "numeric" : field.type === "tel" ? "tel" : undefined}
          onChange={(event) => updateEnquiryField(field.key, event.target.value)}
        />
      )}
    </label>
    );
  };

  const renderContactStep = () => {
    const methods = enquiryForm.preferred_contact_methods || [];
    const toggleContactMethod = (method) => {
      setEnquiryForm((prev) => {
        const current = prev.preferred_contact_methods || [];
        return {
          ...prev,
          preferred_contact_methods: current.includes(method)
            ? current.filter((item) => item !== method)
            : [...current, method],
        };
      });
      setEnquiryError("");
      if (config.funnelTrackingEnabled) {
        trackFunnelEvent("enquiry_contact_method_selected", {
          flow_version: flowVersion, cta_version: ctaVersion, brand: config.chatBrand || "ccg", quote_id: quote?.quote_id,
          vehicle_code: enquiry?.card?.code, contact_method: method,
        });
      }
    };
    return (
      <div className="premium-enquiry-contact">
        <div className="premium-enquiry-reassurance">
          <strong>Final step — where should we send your confirmed price?</strong>
          <span>Phone and email give us a backup if one detail is mistyped. No marketing.</span>
        </div>
        {renderEnquiryField({ key: "name", label: "Your name", type: "text", placeholder: "Name" })}
        <div className="premium-enquiry-phone-grid">
          <label>
            <span>Country</span>
            <select value={enquiryForm.phone_country || "GB"} onChange={(event) => updateEnquiryField("phone_country", event.target.value)}>
              {PHONE_COUNTRIES.map((country) => <option key={country.value} value={country.value}>{country.label}</option>)}
            </select>
          </label>
          {renderEnquiryField({
            key: "phone", label: "Phone", type: "tel",
            placeholder: enquiryForm.phone_country === "GB" ? "07700 900123" : "+ country code and number",
          })}
        </div>
        {renderEnquiryField({ key: "email", label: "Email", type: "email", placeholder: "you@example.com" })}
        <fieldset>
          <legend>How may we contact you? <small>Select one or more</small></legend>
          <div className="premium-enquiry-contact-options">
            {CONTACT_OPTIONS.map((option) => (
              <label key={option.value} className={methods.includes(option.value) ? "is-selected" : ""}>
                <input
                  type="checkbox"
                  name="preferred_contact_methods"
                  value={option.value}
                  checked={methods.includes(option.value)}
                  onChange={() => toggleContactMethod(option.value)}
                />
                <span><strong>{option.label}</strong></span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>
    );
  };

  if (loading) {
    return (
      <main className="premium-results-shell">
        <div className="premium-results-loading">
          <h1>Fetching your fare options...</h1>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="premium-results-shell">
        <div className="premium-results-loading">
          <h1>Quote unavailable</h1>
          <p>{error}</p>
          <button type="button" onClick={() => navigate("/")}>Start new quote</button>
        </div>
      </main>
    );
  }

  if (!quote) return null;
  const discountNudge = buildDiscountNudge(quote, config);
  const primaryAvailableCard = resultCards.find((card) => card.available !== false) || null;
  const primaryDisplayCard = buildDisplayCard(primaryAvailableCard, config, quote);
  const bestRateIntent = config.enquiryIntentEnabled
    ? {
        id: "best_rate",
        note: "Please check today’s best available partner rate for this journey.",
        requestedOptions: ["best available partner rate"],
      }
    : null;
  return (
    <main className={`premium-results-shell ${config.className || ""} ${config.mobileConversionLayout ? "has-mobile-conversion-layout" : ""}`}>
      <style>{premiumResultsCss}</style>
      <FloatingSupportChat
        brand={config.chatBrand}
        defaultFlow="enquiry"
        position="right"
        showAfterMs={config.chatNudgeDisabled ? 0 : 15000}
      />

      <section className="premium-results-trust" aria-label={`${config.brandName} trust signals`}>
        {config.trustItems.map((item) => <TrustItem key={item.title} {...item} />)}
      </section>

      <section className="premium-results-panel">
        <header className="premium-results-head">
          <div>
            <span className="premium-results-check">✓</span>
            <h1>{headline}</h1>
            <p>{config.resultsIntro || "Prices include driver, fuel, airport charges and 60 minutes free waiting time."}</p>
            <strong>{formatPostcode(quote.request?.pickup)} → {formatPostcode(quote.request?.dropoff)}</strong>
            {discountNudge ? (
              <p className="premium-results-discount-nudge">
                <span>{discountNudge}</span>
                {config.discountNudgeActionLabel && primaryDisplayCard ? (
                  <button
                    type="button"
                    onClick={() => openEnquiry(primaryDisplayCard, "enquiry", bestRateIntent)}
                  >
                    {config.discountNudgeActionLabel}
                  </button>
                ) : null}
              </p>
            ) : null}
          </div>
          <aside>
            <b>↗</b>
            <span>
              <strong>{config.routeConfidenceTitle || "Route confidence"}: <em>{config.routeConfidenceValue || "High"}</em></strong>
              <small>{config.routeConfidenceBody || "Live traffic and flight data looks good"}</small>
            </span>
          </aside>
        </header>

        <ResultsLiveTrustStrip brand={config.chatBrand || "ccg"} quote={quote} paused={!!enquiry} />

        <PremiumContactStrip trackingContext={{ brand: config.chatBrand, quote_id: quote.quote_id }} tone="light" className="premium-results-contact-strip" />

        <div className="premium-results-list">
          {resultCards.map((card) => {
            const detailsKey = card.details_key || card.code;
            const details = {
              ...fallbackVehicleDetails[detailsKey],
              ...(config.vehicles?.[detailsKey] || {}),
              ...(card.option_details || {}),
            };
            const displayPricing = getDisplayPricing(card, config);
            const displayCard = buildDisplayCard(card, config, quote);
            const isEnquiry =
              !!resolveConfigValue(config.forceEnquiryOnly, { card, quote }) ||
              card.book_mode === "enquiry";
            const primaryGoesToBooking = isEnquiry && config.enquiryPrimaryAction === "booking";
            const publicPriceVisible = displayCard.pricing_display.public_price_visible;
            const publicPriceReason = displayCard.pricing_display.public_price_reason;
            const configuredNoPriceButtonLabel = resolveConfigValue(
              config.noPriceButtonLabel,
              { card: displayCard, quote }
            );
            const configuredEnquiryButtonLabel = resolveConfigValue(
              config.enquiryButtonLabel,
              { card: displayCard, quote }
            );
            const buttonLabel = isEnquiry
              ? primaryGoesToBooking
                ? config.enquiryBookingLabel || "Request booking"
                : !publicPriceVisible && configuredNoPriceButtonLabel
                  ? configuredNoPriceButtonLabel
                  : configuredEnquiryButtonLabel || "Send enquiry"
              : "Book now";
            const secondaryLabel = isEnquiry
              ? resolveConfigValue(config.callbackButtonLabel, { card: displayCard, quote }) || "Request callback"
              : "Ask a question";
            const cardIntent =
              displayCard.enquiry_intent ||
              (config.cardSpecificEnquiryIntent
                ? {
                    id: `check_${displayCard.display_key || displayCard.code}`,
                    note: `Please check availability and today’s best rate for the ${displayCard.display_title}.`,
                    requestedOptions: [displayCard.display_title],
                  }
                : bestRateIntent);
            return (
              <article
                className={`premium-result-card is-${details.badgeTone || "green"}`}
                key={card.display_key || card.code}
                data-funnel-result-card={card.display_key || card.code}
                data-funnel-vehicle={card.code}
                data-funnel-fare={publicPriceVisible ? displayPricing.displayPrice : ""}
                data-funnel-price-visible={publicPriceVisible ? "true" : "false"}
                data-funnel-price-reason={publicPriceReason}
              >
                <div className="premium-result-image">
                  <img src={details.image} alt={details.title} />
                  <span>{details.badge}</span>
                </div>

                <div className="premium-result-main">
                  <h2>{details.title || card.label}</h2>
                  <p>{details.subtitle || formatReason(card)}</p>
                  <div className="premium-result-pills">
                    <span>♙ {details.passengers}</span>
                    <span>▣ {details.bags}</span>
                    <span>✣ Air Conditioning</span>
                    <span>♙ Professional Driver</span>
                    <span>⌖ Meet & Greet</span>
                    <span>✣ Flight Monitoring</span>
                  </div>
                  <div className="premium-result-fit">
                    <b>♧</b>
                    <span>
                      <strong>{details.highlight}</strong>
                      <small>{details.similar}</small>
                    </span>
                  </div>
                </div>

                <div className={`premium-result-price${publicPriceVisible ? "" : " is-request-only"}`}>
                  {publicPriceVisible ? (
                    <>
                      <small>{isEnquiry ? config.publicPriceEyebrow || (displayCard.price_type === "from" ? "From" : "Estimated from") : "Fixed all-inclusive price"}</small>
                      <strong>{isEnquiry ? formatMoney(displayPricing.displayPrice) : formatPrice(card)}</strong>
                      <span>{isEnquiry ? config.priceSubtext || details.proof : "Total price"}</span>
                      {isEnquiry ? <em>{config.priceEmphasis || details.proof}</em> : null}
                    </>
                  ) : (
                    <>
                      <small>{resolveConfigValue(config.noPriceEyebrow, { card: displayCard, quote }) || "Coach quotation"}</small>
                      <strong>{card.available === false ? "Manual availability check" : resolveConfigValue(config.noPriceLabel, { card: displayCard, quote }) || "Available to request"}</strong>
                      <span>{resolveConfigValue(config.noPriceSubtext, { card: displayCard, quote }) || "Final vehicle and rate confirmed by our team"}</span>
                    </>
                  )}
                </div>

                <div className="premium-result-actions">
                  <button type="button" disabled={!card.available} onClick={() => (isEnquiry && !primaryGoesToBooking ? openEnquiry(displayCard, "enquiry", cardIntent) : handleSelect(card))}>
                    <span>{isEnquiry ? "▱" : "▣"}</span> {buttonLabel}
                  </button>
                  {whatsappQuoteEnabled && isEnquiry ? (
                    <a className="secondary premium-whatsapp-quote"
                      aria-label="Get my quote directly on WhatsApp"
                      href={card.available ? whatsAppHref(displayCard, availableFormFor(displayCard)) : undefined}
                      aria-disabled={!card.available}
                      onClick={(event) => {
                        if (!card.available) { event.preventDefault(); return; }
                        recordWhatsApp(displayCard, availableFormFor(displayCard));
                      }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M20.5 11.5a8.5 8.5 0 0 1-12.7 7.4L3 20l1.2-4.6A8.5 8.5 0 1 1 20.5 11.5Z"/><path d="M8 7.5c-.8 1.8 1.2 5.8 4.5 7.3 1.5.7 2.6.4 3-1l-2-1-1 1c-1.8-.8-3-2-3.5-3.5l1-1-1-2Z"/></svg>
                      <span>Get my quote directly</span>
                    </a>
                  ) : !config.hideSecondaryAction ? (
                    <button
                      type="button"
                      className="secondary"
                      disabled={!card.available}
                      onClick={() => openEnquiry(
                        displayCard,
                        isEnquiry
                          ? resolveConfigValue(config.secondaryEnquiryMode, { card: displayCard, quote }) || "callback"
                          : "enquiry",
                        null
                      )}
                    >
                      ▱ {secondaryLabel}
                    </button>
                  ) : null}
                  <ul>
                    {(details.bullets || []).map((item) => <li key={item}>✓ {item}</li>)}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>

        {config.comparisonOptions?.length && primaryDisplayCard ? (
          <section className="premium-results-comparison" aria-labelledby="premium-results-comparison-title">
            <header>
              <h2 id="premium-results-comparison-title">{config.comparisonTitle || "Other options we can check"}</h2>
              {config.comparisonBody ? <p>{config.comparisonBody}</p> : null}
            </header>
            <div>
              {config.comparisonOptions.map((option) => (
                <article key={option.id}>
                  <span aria-hidden="true">✓</span>
                  <h3>{option.title}</h3>
                  <p>{option.body}</p>
                  <button
                    type="button"
                    onClick={() => openEnquiry(primaryDisplayCard, "enquiry", option)}
                  >
                    {option.actionLabel || "Check this option"} →
                  </button>
                </article>
              ))}
            </div>
            <small>Options are checked against your final passenger count, luggage and vehicle availability.</small>
          </section>
        ) : null}

        <footer className="premium-results-inclusions">
          {inclusions.map((item) => <span key={item}>{item}</span>)}
        </footer>
        <p className="premium-results-smallprint">
          {config.estimateDisclaimer ||
            "Enquiry prices are estimates only and remain subject to vehicle availability, driver coverage, journey timing, route conditions and final confirmation by our team."}
        </p>
      </section>

      <section className="premium-results-proof">
        <img src={config.driverImage || "/logo.jpeg"} alt={`${config.brandName} driver`} />
        <div>
          <h2>{config.priorityTitle}</h2>
          <p>{config.priorityBody}</p>
        </div>
        <dl>
          {proofStats.map((item) => (
            <div key={item.label}><dt>{item.value}</dt><dd>{item.label}</dd></div>
          ))}
        </dl>
        <aside className="premium-google-review">
          <strong><span>G</span> Google</strong>
          <div>★★★★★</div>
          <small>
            <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer">4.8 rating on Google reviews</a>
          </small>
        </aside>
      </section>

      <p className="premium-results-secure">▣ Your details are secure and never shared. <span /> Trusted by thousands of travellers every day.</p>

      {enquiry ? (
        <div className="premium-enquiry-modal" role="dialog" aria-modal="true" aria-label="Send journey enquiry">
          <div className="premium-enquiry-backdrop" onClick={closeEnquiry} />
          <section className={`premium-enquiry-panel ${enquiryImprovements ? "premium-enquiry-v2" : ""}`}>
            <header>
              <div>
                <small>{enquiry.intent?.id === "compare_options" || enquiry.intent?.id === "mixed_vehicles" ? "Compare group options" : enquiry.mode === "callback" ? "Request callback" : enquiry.mode === "quick_enquiry" ? config.secondaryEnquiryModalLabel || "Send a quick enquiry" : resolveConfigValue(config.enquiryModalLabel, { card: enquiry.card, quote }) || "Quick enquiry"}</small>
                <h2>{enquirySuccess ? "Enquiry received" : enquiry.card.display_title || enquiry.card.label}</h2>
                <p>{formatJourneyLocation(quote.request?.pickup, config)} → {formatJourneyLocation(quote.request?.dropoff, config)}</p>
                {enquiryImprovements && <div className="enquiry-journey-summary">
                  <span>{enquiryForm.confirmed_counts?.passengers ? `${enquiryForm.passengers} passengers` : "Passengers to confirm"} · {enquiryForm.confirmed_counts?.bags ? `${enquiryForm.bags} bags` : "Bags to confirm"}</span>
                  <span>{formatQuotePickupTime(enquiryForm.pickup_time)}</span>
                </div>}
                {!enquirySuccess && enquiry.card?.pricing_display?.public_price_visible !== false && enquiry.card?.price != null ? (
                  <p><strong>Estimated fare: {formatMoney(enquiry.card.price)}</strong></p>
                ) : null}
                {!enquirySuccess && resolveConfigValue(config.enquiryReassurance, { card: enquiry.card, quote }) ? <p>{resolveConfigValue(config.enquiryReassurance, { card: enquiry.card, quote })}</p> : null}
              </div>
              <button type="button" onClick={closeEnquiry} aria-label="Close enquiry">×</button>
            </header>

            {enquirySuccess ? (
              <div className="premium-enquiry-success">
                <b>✓</b>
                <h3>We have your details</h3>
                <p>Reference: <strong>{enquirySuccess.refCode}</strong></p>
                <p>We will check this option and come back shortly.</p>
                <button type="button" onClick={closeEnquiry}>Done</button>
              </div>
            ) : (
              <>
                <div className="premium-enquiry-progress">
                  <span style={{ width: `${((enquiryStep + 1) / enquirySteps.length) * 100}%` }} />
                </div>
                <div className="premium-enquiry-step">
                  <small>Step {enquiryStep + 1} of {enquirySteps.length}</small>
                  <h3>{enquiryImprovements ? ({ phone: "Your mobile number", email: "Your email address" }[currentEnquiryStep.key] || currentEnquiryStep.label) : currentEnquiryStep.label}</h3>
                  {enquiryImprovements && ["name", "phone", "email"].includes(currentEnquiryStep.key) && <p className="enquiry-field-help">{{ name: "So we know who the quote is for.", phone: "We’ll usually contact you on WhatsApp about your quote.", email: "For your written quote and journey summary." }[currentEnquiryStep.key]}</p>}
                  {currentEnquiryStep.contact
                    ? renderContactStep()
                    : (currentEnquiryStep.fields || [currentEnquiryStep]).map(renderEnquiryField)}
                  {enquiryImprovements && currentEnquiryStep.key === "notes" && <>
                    <label className="enquiry-return"><input type="checkbox" checked={!!enquiryForm.return_requested} onChange={event => updateEnquiryField("return_requested", event.target.checked)} /><span>I also need a return journey</span></label>
                    {enquiryForm.return_requested && <p className="enquiry-field-help">Add the return date/time in your notes if known. Otherwise, we’ll confirm it with you.</p>}
                    <p className="enquiry-field-help"><strong>Journey details complete.</strong> We’ll check vehicle availability and an accurate fare. Next, tell us how to contact you.</p>
                  </>}
                  {enquiryImprovements && currentEnquiryStep.key === "email" && <p className="enquiry-field-help">{ENQUIRY_RESPONSE_TIME}</p>}
                  {enquiryError ? <div className="premium-enquiry-error">{enquiryError}</div> : null}
                </div>
                {inFlowWhatsAppEnabled && ["name", "phone", "email"].includes(currentEnquiryStep.key) && (
                  <div className="enquiry-whatsapp-alternative">
                    <a href={whatsAppHref(enquiry.card, enquiryForm)}
                      onClick={() => recordWhatsApp(enquiry.card, enquiryForm, currentEnquiryStep.key, "enquiry_flow")}>
                      Continue on WhatsApp
                    </a>
                    <small>Send your journey details in WhatsApp.</small>
                  </div>
                )}
                {enquiryImprovements ? <a className="premium-enquiry-compact-reassurance" href="/privacy" target="_blank" rel="noopener noreferrer">Only journey-related contact</a> : resolveConfigValue(config.compactEnquiryReassurance, { card: enquiry.card, quote }) ? (
                  <small className="premium-enquiry-compact-reassurance">
                    {resolveConfigValue(config.compactEnquiryReassurance, { card: enquiry.card, quote })}
                  </small>
                ) : null}
                <footer>
                  <button type="button" className="secondary" onClick={enquiryStep === 0 ? closeEnquiry : backEnquiryStep} disabled={enquirySubmitting}>
                    {enquiryStep === 0 ? "Cancel" : "Back"}
                  </button>
                  <button type="button" onClick={nextEnquiryStep} disabled={enquirySubmitting}>
                    {enquirySubmitting ? "Sending..." : enquiryAtLastStep ? (enquiryImprovements ? "Request my quote" : "Send enquiry") : "Next"}
                  </button>
                </footer>
              </>
            )}
          </section>
        </div>
      ) : null}
    </main>
  );
};

const premiumResultsCss = `
  .enquiry-whatsapp-alternative { padding: 0 24px 12px; text-align: center; }
  .enquiry-whatsapp-alternative a { display: inline-block; padding: 10px 16px; border: 1px solid #087f46; border-radius: 8px; color: #087f46; background: #effbf4; font-weight: 700; text-decoration: none; }
  .enquiry-whatsapp-alternative a:focus-visible { outline: 3px solid #07142f; outline-offset: 3px; }
  .enquiry-whatsapp-alternative small { display: block; margin-top: 5px; }

  .premium-results-shell {
    min-height: 100vh;
    padding: 12px 16px 28px;
    background: #f8fbff;
    color: #07142f;
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }
  .premium-results-shell * { box-sizing: border-box; }
  .premium-results-trust,
  .premium-results-panel,
  .premium-results-proof,
  .premium-results-secure {
    width: min(100%, 1660px);
    margin-left: auto;
    margin-right: auto;
  }
  .premium-results-trust {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0;
    min-height: 88px;
    border: 1px solid rgba(211, 222, 238, 0.9);
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 14px 36px rgba(15, 38, 74, 0.08);
    overflow: hidden;
  }
  .premium-results-trust-item {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
    padding: 18px 24px;
    border-right: 1px solid #dfe7f3;
  }
  .premium-results-trust-item:last-child { border-right: 0; }
  .premium-results-trust-item b {
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    display: grid;
    place-items: center;
    color: #0b55d9;
    font-size: 26px;
  }
  .premium-results-trust-item:first-child b { color: #f5b400; }
  .premium-results-trust-item span { min-width: 0; display: grid; gap: 3px; }
  .premium-results-trust-item strong { font-size: 16px; line-height: 1.2; }
  .premium-results-trust-item small { color: #53678a; font-size: 14px; font-weight: 650; }
  .premium-results-trust-item a,
  .premium-google-review a {
    color: inherit;
    text-decoration: none;
  }
  .premium-results-trust-item a:hover,
  .premium-google-review a:hover {
    text-decoration: underline;
  }
  .premium-results-panel {
    margin-top: 24px;
    padding: 20px;
    border: 1px solid rgba(211, 222, 238, 0.9);
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 16px 42px rgba(15, 38, 74, 0.08);
  }
  .premium-live-trust-strip {
    height: 44px;
    margin: 12px 0 10px;
    padding: 9px 14px;
    display: flex;
    align-items: center;
    gap: 10px;
    overflow: hidden;
    border: 1px solid #dce7f5;
    border-radius: 10px;
    background: #f7faff;
    color: #102348;
  }
  .premium-live-trust-icon {
    width: 22px;
    height: 22px;
    flex: 0 0 22px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #e1f8ec;
    color: #07864d;
    font-size: 12px;
    font-weight: 900;
  }
  .premium-live-trust-strip small {
    flex: 0 0 auto;
    color: #607394;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: .07em;
    text-transform: uppercase;
  }
  .premium-live-trust-strip strong {
    min-width: 0;
    overflow: hidden;
    color: #14284c;
    font-size: 13px;
    font-weight: 780;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .premium-live-trust-strip i {
    margin-left: auto;
    color: #07864d;
    font-size: 10px;
    font-style: normal;
    font-weight: 850;
    letter-spacing: .08em;
    text-transform: uppercase;
  }
  .premium-results-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    padding: 0 0 18px;
  }
  .premium-results-check {
    width: 28px;
    height: 28px;
    display: inline-grid;
    place-items: center;
    margin-right: 10px;
    border: 2px solid #10b981;
    border-radius: 999px;
    color: #10b981;
    font-weight: 950;
  }
  .premium-results-head h1 {
    display: inline;
    margin: 0;
    color: #081534;
    font-size: 28px;
    line-height: 1.18;
  }
  .premium-results-head p {
    margin: 12px 0 6px 40px;
    color: #273d66;
    font-size: 16px;
    font-weight: 650;
  }
  .premium-results-head > div > strong {
    display: block;
    margin-left: 40px;
    color: #07142f;
    font-size: 14px;
  }
  .premium-results-head .premium-results-discount-nudge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin: 12px 0 0 40px;
    padding: 9px 12px;
    border: 1px solid #fde68a;
    border-radius: 999px;
    background: #fffbeb;
    color: #7c4a03;
    font-size: 13px;
    font-weight: 850;
  }
  .premium-results-discount-nudge button {
    display: inline-flex !important;
    width: auto !important;
    min-width: 0 !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none !important;
    color: #9a5b00;
    font: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }
  .premium-results-discount-nudge button:hover,
  .premium-results-discount-nudge button:focus-visible {
    color: #6b3f00;
  }
  .premium-results-head aside {
    min-width: 290px;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 18px;
    border-radius: 10px;
    background: #edf5ff;
    color: #07142f;
  }
  .premium-results-head aside b { color: #0b55d9; font-size: 30px; }
  .premium-results-head aside span { display: grid; gap: 4px; }
  .premium-results-head aside em { color: #039855; font-style: normal; }
  .premium-results-head aside small { color: #07142f; font-size: 13px; font-weight: 650; }
  .premium-results-list { display: grid; gap: 16px; }
  .premium-result-card {
    display: grid;
    grid-template-columns: 390px minmax(360px, 1fr) 240px 300px;
    gap: 18px;
    align-items: center;
    padding: 20px;
    border: 1px solid rgba(211, 222, 238, 0.95);
    border-left: 3px solid #10b981;
    border-radius: 12px;
    background: #ffffff;
  }
  .premium-result-card.is-rose { border-left-color: #ff4778; }
  .premium-result-image {
    position: relative;
    min-height: 220px;
    overflow: hidden;
    border-radius: 9px;
    background: #e7eef8;
  }
  .premium-result-image img {
    width: 100%;
    height: 100%;
    min-height: 220px;
    object-fit: cover;
    display: block;
  }
  .premium-result-image span {
    position: absolute;
    top: 12px;
    left: 12px;
    padding: 8px 14px;
    border-radius: 999px;
    background: #fff8dc;
    color: #07142f;
    font-size: 13px;
    font-weight: 900;
  }
  .is-rose .premium-result-image span { background: #fff0f4; color: #9f1239; }
  .premium-result-main {
    min-width: 0;
    display: grid;
    gap: 10px;
  }
  .premium-result-main h2 { margin: 0; color: #06133a; font-size: 28px; line-height: 1.1; }
  .premium-result-main p { margin: 0; color: #31476d; font-size: 16px; font-weight: 650; }
  .premium-result-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
  }
  .premium-result-pills span {
    min-height: 32px;
    display: inline-flex;
    align-items: center;
    padding: 0 12px;
    border-radius: 8px;
    background: #f3f6fb;
    color: #07142f;
    font-size: 13px;
    font-weight: 800;
  }
  .premium-result-fit {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-top: 8px;
    padding: 16px;
    border-radius: 9px;
    background: #ecfdf3;
    color: #07142f;
  }
  .is-rose .premium-result-fit { background: #fff1f2; }
  .premium-result-fit b { color: #10b981; font-size: 24px; }
  .is-rose .premium-result-fit b { color: #ff4778; }
  .premium-result-fit span { display: grid; gap: 4px; }
  .premium-result-fit small { color: #465a7d; font-size: 13px; font-weight: 700; }
  .premium-result-price {
    min-height: 220px;
    display: grid;
    align-content: center;
    justify-items: center;
    gap: 7px;
    padding: 0 18px;
    border-left: 1px solid #d9e2ef;
    text-align: center;
  }
  .premium-result-price small { color: #07142f; font-size: 14px; font-weight: 750; }
  .premium-result-price strong { color: #06133a; font-size: 38px; line-height: 1; }
  .premium-result-price span { color: #53678a; font-size: 14px; font-weight: 800; }
  .premium-result-price em { color: #039855; font-size: 14px; font-style: normal; font-weight: 850; }
  .is-rose .premium-result-price em { color: #ff4778; }
  .premium-result-price.is-request-only strong {
    max-width: 210px;
    color: #047857;
    font-size: 25px;
    line-height: 1.15;
  }
  .premium-result-price.is-request-only span {
    max-width: 210px;
    line-height: 1.4;
  }
  .premium-result-actions {
    display: grid;
    gap: 12px;
  }
  .premium-result-actions button {
    min-height: 58px;
    border: 0;
    border-radius: 9px;
    background: #ffc107;
    color: #07142f;
    font-size: 18px;
    font-weight: 950;
    cursor: pointer;
  }
  .premium-result-actions button.secondary {
    background: #f0fdf4;
    border: 1px solid #86efac;
    color: #065f46;
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.10);
  }
  .premium-result-actions button.secondary:hover {
    background: #dcfce7;
    border-color: #4ade80;
    box-shadow: 0 10px 24px rgba(16, 185, 129, 0.16);
  }
  .premium-result-actions button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  .premium-result-actions ul {
    display: grid;
    gap: 8px;
    margin: 6px 0 0;
    padding: 0;
    list-style: none;
    color: #53678a;
    font-size: 14px;
    font-weight: 700;
  }
  .premium-results-comparison {
    margin-top: 18px;
    padding: 18px;
    border: 1px solid #d9e2ef;
    border-radius: 12px;
    background: #f8fbff;
  }
  .premium-results-comparison > header {
    display: block !important;
    padding: 0 !important;
    border: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }
  .premium-results-comparison > header h2 {
    margin: 0;
    color: #06133a;
    font-size: 21px;
  }
  .premium-results-comparison > header p {
    margin: 6px 0 0;
    max-width: 920px;
    color: #465a7d;
    font-size: 14px;
    line-height: 1.45;
    font-weight: 650;
  }
  .premium-results-comparison > div {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-top: 14px;
  }
  .premium-results-comparison article {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 6px 10px;
    align-content: start;
    padding: 15px;
    border: 1px solid #d7e2f1;
    border-radius: 10px;
    background: #ffffff;
  }
  .premium-results-comparison article > span {
    grid-row: 1 / span 2;
    width: 24px;
    height: 24px;
    display: grid;
    place-items: center;
    border-radius: 999px;
    background: #dcfce7;
    color: #047857;
    font-weight: 950;
  }
  .premium-results-comparison h3 {
    margin: 1px 0 0;
    color: #07142f;
    font-size: 16px;
  }
  .premium-results-comparison article p {
    margin: 0;
    color: #53678a;
    font-size: 13px;
    line-height: 1.4;
    font-weight: 650;
  }
  .premium-results-comparison article button {
    grid-column: 2;
    display: inline-flex !important;
    width: auto !important;
    min-width: 0 !important;
    min-height: 0 !important;
    justify-self: start;
    margin: 5px 0 0 !important;
    padding: 0 !important;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none !important;
    color: #075f43;
    font-size: 13px;
    font-weight: 900;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
  }
  .premium-results-comparison > small {
    display: block;
    margin-top: 12px;
    color: #6b7280;
    font-size: 11px;
    line-height: 1.4;
  }
  .premium-results-inclusions {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
    margin-top: 18px;
    padding: 16px;
    border-radius: 9px;
    background: #f0f6ff;
    color: #31476d;
    text-align: center;
    font-size: 14px;
    font-weight: 750;
  }
  .premium-results-proof {
    display: grid;
    grid-template-columns: 140px minmax(240px, 1fr) minmax(420px, 1.4fr) 280px;
    gap: 22px;
    align-items: center;
    margin-top: 20px;
    padding: 18px 24px;
    border: 1px solid rgba(211, 222, 238, 0.9);
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 16px 42px rgba(15, 38, 74, 0.08);
  }
  .premium-results-proof > img {
    width: 112px;
    height: 112px;
    border-radius: 999px;
    object-fit: cover;
  }
  .premium-results-proof h2 { margin: 0 0 8px; color: #06133a; font-size: 22px; }
  .premium-results-proof p { margin: 0; color: #31476d; font-size: 15px; line-height: 1.45; font-weight: 650; }
  .premium-results-proof dl {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin: 0;
  }
  .premium-results-proof dl div {
    padding: 0 22px;
    border-left: 1px solid #d9e2ef;
    text-align: center;
  }
  .premium-results-proof dt { color: #06133a; font-size: 24px; font-weight: 950; }
  .premium-results-proof dd { margin: 4px 0 0; color: #07142f; font-size: 13px; font-weight: 700; }
  .premium-google-review { display: grid; gap: 7px; justify-items: start; }
  .premium-google-review strong { color: #07142f; font-size: 17px; }
  .premium-google-review strong span { color: #4285f4; }
  .premium-google-review div {
    padding: 4px 8px;
    border-radius: 4px;
    background: #fbbc04;
    color: #ffffff;
    letter-spacing: 2px;
    font-size: 15px;
  }
  .premium-google-review small { color: #07142f; font-size: 13px; font-weight: 800; }
  .premium-results-secure {
    display: flex;
    justify-content: center;
    gap: 30px;
    margin-top: 20px;
    color: #53678a;
    font-size: 14px;
    font-weight: 700;
  }
  .premium-results-secure span { width: 1px; background: #cdd8e7; }
  .premium-results-smallprint {
    margin: 10px auto 0;
    max-width: 980px;
    color: #6b7280;
    font-size: 11px;
    line-height: 1.45;
    text-align: center;
  }
  .premium-results-loading {
    width: min(100%, 760px);
    margin: 70px auto;
    padding: 28px;
    border: 1px solid #d9e2ef;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 16px 42px rgba(15, 38, 74, 0.08);
  }
  .premium-results-loading h1 { margin: 0 0 8px; }
  .premium-results-loading p { color: #9f1239; }
  .premium-results-loading button {
    min-height: 44px;
    padding: 0 18px;
    border: 0;
    border-radius: 8px;
    background: #ffc107;
    color: #07142f;
    font-weight: 900;
    cursor: pointer;
  }
  .premium-enquiry-modal {
    position: fixed;
    inset: 0;
    z-index: 10050;
    display: grid;
    place-items: center;
    padding: 18px;
  }
  .premium-enquiry-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(7, 20, 47, 0.48);
    backdrop-filter: blur(5px);
  }
  .premium-enquiry-panel {
    position: relative;
    width: min(100%, 460px);
    max-height: calc(100dvh - 24px);
    overflow-x: hidden;
    overflow-y: auto;
    border: 1px solid rgba(211, 222, 238, 0.95);
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 28px 70px rgba(7, 20, 47, 0.28);
  }
  .premium-enquiry-panel header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    padding: 18px 18px 14px;
    background: linear-gradient(135deg, #f8fbff, #ffffff);
    border-bottom: 1px solid #dfe7f3;
  }
  .premium-enquiry-panel header small {
    display: block;
    color: #0b55d9;
    font-size: 12px;
    font-weight: 950;
    text-transform: uppercase;
  }
  .premium-enquiry-panel header h2 {
    margin: 5px 0 0;
    color: #07142f;
    font-size: 22px;
    line-height: 1.15;
  }
  .premium-enquiry-panel header p {
    margin: 7px 0 0;
    color: #42577f;
    font-size: 13px;
    font-weight: 800;
  }
  .premium-enquiry-panel header button {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    border: 1px solid #d9e2ef;
    border-radius: 999px;
    background: #ffffff;
    color: #07142f;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
  }
  .premium-enquiry-progress {
    height: 5px;
    background: #edf3fb;
  }
  .premium-enquiry-progress span {
    display: block;
    height: 100%;
    background: #10b981;
    transition: width 0.18s ease;
  }
  .premium-enquiry-step {
    display: grid;
    gap: 12px;
    padding: 18px;
  }
  .premium-enquiry-step > small {
    color: #53678a;
    font-size: 12px;
    font-weight: 850;
  }
  .premium-enquiry-step > h3 { margin: 0; color: #07142f; font-size: 18px; }
  .premium-enquiry-contact { display: grid; gap: 9px; }
  .premium-enquiry-reassurance {
    display: grid; gap: 3px; padding: 9px 11px; border: 1px solid #bde7cf;
    border-radius: 10px; background: #effbf4; color: #075f3c;
  }
  .premium-enquiry-reassurance span { color: #315f4d; font-size: 13px; }
  .premium-enquiry-contact fieldset { margin: 0; padding: 0; border: 0; }
  .premium-enquiry-contact legend { margin-bottom: 6px; color: #07142f; font-size: 13px; font-weight: 900; }
  .premium-enquiry-contact legend small { color: #64748b; font-size: 11px; font-weight: 700; }
  .premium-enquiry-contact-options { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
  .premium-enquiry-contact-options label {
    display: flex; align-items: center; justify-content: center; gap: 6px; padding: 7px 8px;
    border: 1px solid #d8e1ee; border-radius: 9px; background: #fff; cursor: pointer;
  }
  .premium-enquiry-contact-options label.is-selected { border-color: #18a566; background: #effbf4; }
  .premium-enquiry-contact-options input { width: auto; min-height: 0; margin: 0; padding: 0; }
  .premium-enquiry-contact-options span { display: grid; gap: 2px; }
  .premium-enquiry-contact-options label span { font-size: 13px; }
  .premium-enquiry-contact-options small { color: #64748b; font-size: 10px; line-height: 1.2; }
  .premium-enquiry-phone-grid { display: grid; grid-template-columns: minmax(145px, .78fr) 1.22fr; gap: 8px; }
  .premium-enquiry-phone-grid label { display: grid; gap: 7px; color: #07142f; font-weight: 800; }
  .premium-enquiry-step select {
    width: 100%; min-height: 54px; padding: 0 12px; border: 1px solid #cdd8e7;
    border-radius: 10px; background: #fff; color: #07142f; font: inherit;
  }
  .premium-enquiry-step label {
    display: grid;
    gap: 9px;
  }
  .premium-enquiry-step label span {
    color: #07142f;
    font-size: 18px;
    font-weight: 950;
  }
  .premium-enquiry-step input,
  .premium-enquiry-step textarea {
    width: 100%;
    border: 1px solid #cdd8e7;
    border-radius: 10px;
    background: #ffffff;
    color: #07142f;
    font: inherit;
    font-size: 16px;
    font-weight: 750;
    outline: none;
  }
  .premium-enquiry-step input {
    min-height: 54px;
    padding: 0 14px;
  }
  .premium-enquiry-step textarea {
    resize: vertical;
    min-height: 112px;
    padding: 13px 14px;
    line-height: 1.4;
  }
  .premium-enquiry-step input:focus,
  .premium-enquiry-step textarea:focus {
    border-color: #0b55d9;
    box-shadow: 0 0 0 4px rgba(11, 85, 217, 0.1);
  }
  .premium-enquiry-error {
    padding: 10px 12px;
    border-radius: 9px;
    background: #fff1f2;
    color: #9f1239;
    font-size: 13px;
    font-weight: 850;
  }
  .premium-enquiry-panel footer {
    display: grid;
    grid-template-columns: 1fr 1.25fr;
    gap: 10px;
    padding: 0 18px 18px;
  }
  .premium-enquiry-compact-reassurance {
    display: block;
    margin: -4px 18px 12px;
    color: #64748b;
    font-size: 11px;
    line-height: 1.35;
    text-align: center;
  }
  .premium-enquiry-panel footer button,
  .premium-enquiry-success button {
    min-height: 48px;
    border: 0;
    border-radius: 9px;
    background: #ffc107;
    color: #07142f;
    font-size: 15px;
    font-weight: 950;
    cursor: pointer;
  }
  .premium-enquiry-panel footer button.secondary {
    background: #ffffff;
    border: 1px solid #cdd8e7;
  }
  .premium-enquiry-panel footer button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .premium-enquiry-success {
    display: grid;
    justify-items: center;
    gap: 9px;
    padding: 26px 20px 22px;
    text-align: center;
  }
  .premium-enquiry-success b {
    width: 44px;
    height: 44px;
    display: grid;
    place-items: center;
    border-radius: 999px;
    background: #dcfce7;
    color: #166534;
    font-size: 24px;
  }
  .premium-enquiry-success h3 {
    margin: 4px 0 0;
    color: #07142f;
    font-size: 22px;
  }
  .premium-enquiry-success p {
    margin: 0;
    color: #42577f;
    font-size: 14px;
    font-weight: 750;
  }
  .premium-enquiry-success button {
    width: 100%;
    margin-top: 8px;
  }
  @media (max-width: 1320px) {
    .premium-results-trust { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .premium-result-card { grid-template-columns: 310px minmax(320px, 1fr) 220px; }
    .premium-result-actions { grid-column: 2 / -1; grid-template-columns: repeat(2, minmax(0, 1fr)); align-items: start; }
    .premium-result-actions ul { grid-column: 1 / -1; grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .premium-results-proof { grid-template-columns: 120px minmax(0, 1fr); }
    .premium-results-proof dl,
    .premium-google-review { grid-column: 1 / -1; }
  }
  @media (max-width: 900px) {
    .premium-enquiry-modal { padding: 6px; }
    .premium-enquiry-panel { max-height: calc(100dvh - 12px); border-radius: 12px; }
    .premium-enquiry-panel header { gap: 7px; padding: 8px 10px 7px; }
    .premium-enquiry-panel header small { font-size: 10px; }
    .premium-enquiry-panel header h2 { margin-top: 1px; font-size: 16px; }
    .premium-enquiry-panel header p { margin-top: 2px; font-size: 10px; line-height: 1.2; }
    .premium-enquiry-panel header button { width: 27px; height: 27px; flex-basis: 27px; font-size: 18px; }
    .premium-enquiry-progress { height: 3px; }
    .premium-enquiry-step { gap: 5px; padding: 8px 10px; }
    .premium-enquiry-step > small { font-size: 10px; }
    .premium-enquiry-step > h3 { font-size: 14px; }
    .premium-enquiry-step label { gap: 3px; }
    .premium-enquiry-step label span { font-size: 12px; }
    .premium-enquiry-step input,
    .premium-enquiry-step select { min-height: 38px; height: 38px; padding: 0 9px; font-size: 14px; }
    .premium-enquiry-reassurance { gap: 1px; padding: 6px 8px; font-size: 12px; }
    .premium-enquiry-reassurance span { font-size: 10px; line-height: 1.2; }
    .premium-enquiry-contact { gap: 6px; }
    .premium-enquiry-contact legend { margin-bottom: 4px; font-size: 11px; }
    .premium-enquiry-contact legend small { font-size: 9px; }
    .premium-enquiry-contact-options label { gap: 4px; min-width: 0; padding: 5px 3px; border-radius: 7px; }
    .premium-enquiry-contact-options input[type="checkbox"] {
      position: absolute; width: 1px; height: 1px; min-height: 0; margin: 0;
      padding: 0; opacity: 0; pointer-events: none;
    }
    .premium-enquiry-contact-options label::before {
      content: ""; width: 15px; height: 15px; flex: 0 0 15px;
      display: grid; place-items: center; border: 1.5px solid #8793a5;
      border-radius: 4px; background: #fff; color: #fff;
      font-size: 11px; font-weight: 950; line-height: 1;
    }
    .premium-enquiry-contact-options label.is-selected::before {
      content: "✓"; border-color: #0b8f59; background: #0b8f59;
    }
    .premium-enquiry-contact-options label span { min-width: 0; font-size: 10px; }
    .premium-enquiry-phone-grid { gap: 6px; grid-template-columns: minmax(112px, .72fr) 1.28fr; }
    .premium-enquiry-panel footer { gap: 7px; padding: 0 10px 8px; }
    .premium-enquiry-compact-reassurance { margin: -2px 10px 7px; font-size: 10px; }
    .premium-enquiry-panel footer button { min-height: 38px; font-size: 13px; }
    .premium-enquiry-contact-options { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; }
    .premium-results-shell { padding: 10px 10px 24px; }
    .premium-results-trust {
      display: flex;
      gap: 8px;
      min-height: 0;
      padding: 8px;
      border-radius: 12px;
      overflow-x: auto;
      overflow-y: hidden;
      scroll-snap-type: x proximity;
      -webkit-overflow-scrolling: touch;
    }
    .premium-results-trust::-webkit-scrollbar { display: none; }
    .premium-results-trust-item {
      flex: 0 0 158px;
      min-height: 70px;
      gap: 9px;
      padding: 10px;
      border: 1px solid #dfe7f3;
      border-radius: 10px;
      background: #ffffff;
      scroll-snap-align: start;
    }
    .premium-results-trust-item:last-child { border-right: 1px solid #dfe7f3; }
    .premium-results-trust-item b {
      width: 30px;
      height: 30px;
      flex-basis: 30px;
      font-size: 21px;
    }
    .premium-results-trust-item strong {
      font-size: 12px;
      line-height: 1.1;
    }
    .premium-results-trust-item small {
      font-size: 11px;
      line-height: 1.12;
    }
    .premium-live-trust-strip {
      height: 52px;
      margin: 10px 0 8px;
      padding: 8px 10px;
      gap: 8px;
    }
    .premium-live-trust-strip small { display: none; }
    .premium-live-trust-strip strong {
      min-height: 32px;
      align-content: center;
      line-height: 16px;
      white-space: normal;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
    .premium-live-trust-strip i { font-size: 9px; }
    .premium-results-head { display: grid; }
    .premium-results-head aside { min-width: 0; }
    .premium-results-head h1 { display: block; margin-top: 8px; font-size: 24px; }
    .premium-results-head p,
    .premium-results-head > div > strong,
    .premium-results-head .premium-results-discount-nudge { margin-left: 0; }
    .premium-results-check { margin-right: 0; }
    .premium-result-card { grid-template-columns: 1fr; padding: 14px; }
    .premium-result-image { min-height: 190px; }
    .premium-result-image img { min-height: 190px; }
    .premium-result-main h2 { font-size: 24px; }
    .premium-result-price {
      min-height: auto;
      justify-items: start;
      padding: 14px 0 0;
      border-left: 0;
      border-top: 1px solid #d9e2ef;
      text-align: left;
    }
    .premium-result-actions { grid-column: auto; grid-template-columns: 1fr; }
    .premium-result-actions ul { grid-column: auto; grid-template-columns: 1fr; }
    .has-mobile-conversion-layout .premium-result-card {
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }
    .has-mobile-conversion-layout .premium-result-main { order: 1; }
    .has-mobile-conversion-layout .premium-result-price {
      order: 2;
      padding: 14px;
      border: 1px solid #d9e2ef;
      border-radius: 10px;
      background: #f8fbff;
    }
    .has-mobile-conversion-layout .premium-result-actions { order: 3; }
    .has-mobile-conversion-layout .premium-result-image { order: 4; min-height: 150px; }
    .has-mobile-conversion-layout .premium-result-image img { min-height: 150px; max-height: 180px; }
    .has-mobile-conversion-layout .premium-result-actions ul { display: none; }
    .premium-results-comparison > div { grid-template-columns: 1fr; }
    .premium-results-comparison > header { display: block !important; }
    .premium-results-inclusions { grid-template-columns: 1fr; text-align: left; }
    .premium-results-proof { grid-template-columns: 1fr; text-align: left; }
    .premium-results-proof dl { grid-template-columns: 1fr; gap: 12px; }
    .premium-results-proof dl div { border-left: 0; border-top: 1px solid #d9e2ef; padding: 12px 0 0; text-align: left; }
    .premium-results-secure { display: grid; gap: 8px; text-align: center; }
    .premium-results-secure span { display: none; }
  }

  .premium-result-actions a.premium-whatsapp-quote { display:flex; align-items:center; justify-content:center; gap:8px; padding:16px 12px; min-height:52px; border-radius:12px; background:#087f46; color:white; font-weight:800; text-decoration:none; text-align:center; box-shadow:0 4px 12px #087f4626; }
  .premium-whatsapp-quote:hover { filter:brightness(.92); }
  .premium-whatsapp-quote:focus-visible { outline:3px solid #07142f; outline-offset:3px; }
  .premium-whatsapp-quote[aria-disabled="true"] { opacity:.5; pointer-events:none; }
  .premium-enquiry-v2 .enquiry-journey-summary { display:grid; gap:4px; font-size:13px; margin-top:8px; }
  .premium-enquiry-v2 header p { font-size:13px; line-height:1.4; overflow-wrap:anywhere; }
  .premium-enquiry-v2 .enquiry-field-help { margin:4px 0 8px; font-size:13px; line-height:1.45; color:#42577f; }
  .premium-enquiry-v2 .enquiry-return { display:flex; align-items:center; gap:8px; font-size:14px; }
  .premium-enquiry-v2 .enquiry-return input[type="checkbox"] { width:20px !important; height:20px !important; min-height:20px; padding:0 !important; margin:0 !important; flex:0 0 20px; accent-color:#087f46; }
  .premium-enquiry-v2 .enquiry-return span { font-size:14px; line-height:1.4; }
  .premium-enquiry-v2 input:not([type="checkbox"]), .premium-enquiry-v2 textarea { font-size:16px; }
  .premium-enquiry-v2 .premium-enquiry-compact-reassurance { display:block; text-align:center; font-size:12px; }
`;

export default PremiumQuoteResults;
